import React, { useRef, useEffect, useId, forwardRef, useImperativeHandle, useMemo } from 'react'
import { MessageSquare, SendHorizonal, ImagePlus, X, Check, Rocket, FileText } from 'lucide-react'
import styles from './ConversationInput.module.scss'
import draftPublishStyles from '../DraftPublishButton.module.scss'

const isEditableFocused = (): boolean => {
  const el = document.activeElement
  if (!el) return false
  const tag = el.tagName.toLowerCase()
  if (tag === 'input' || tag === 'textarea') return true
  if (el.getAttribute('contenteditable') === 'true') return true
  return false
}

const PROJECT_AUTOFILL_DOCUMENT_ACCEPT =
  'application/pdf,image/jpeg,image/png,image/webp,.csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

const isPrintableKey = (e: KeyboardEvent): boolean => {
  if (e.ctrlKey || e.metaKey || e.altKey) return false
  if (!e.key || e.key.length !== 1) return false
  const special = ['Enter', 'Tab', 'Escape', 'Backspace', 'Delete', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']
  return !special.includes(e.key)
}

const normalizeFileNameForCompare = (s: string) =>
  s
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

const formatCompactFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes < 0) return ''
  if (bytes < 1024) return `${Math.round(bytes)} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  const mb = bytes / (1024 * 1024)
  return mb >= 10 ? `${Math.round(mb)} MB` : `${Math.round(mb * 10) / 10} MB`
}

export type ConversationInputHandle = {
  openAttachmentPicker: () => void
}

interface ConversationInputProps {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  onKeyPress: (e: React.KeyboardEvent) => void
  disabled?: boolean
  placeholder?: string
  showImageUpload?: boolean
  allowVideo?: boolean
  allowDocuments?: boolean
  highlightProjectAutofillAttachment?: boolean
  hasPendingImage?: boolean
  imageUploadAcknowledged?: boolean
  onImageSelect?: (file: File) => void
  onImageClear?: () => void
  pendingImagePreview?: string | null
  pendingAttachmentMime?: string | null
  pendingAttachmentFileName?: string | null
  pendingAttachmentSizeBytes?: number | null
  lastAnalyzedDocumentationFileName?: string | null
  readyToExecute?: boolean
  onConfirmExecution?: () => void
  showDraftPublish?: boolean
  draftPublishing?: boolean
  onPublishDraft?: () => void
  onOpenHistory?: () => void
  historyCount?: number
}

const ConversationInput = forwardRef<ConversationInputHandle, ConversationInputProps>(
  (
    {
      value,
      onChange,
      onSend,
      onKeyPress,
      disabled = false,
      placeholder = 'Escribí tu mensaje...',
      showImageUpload = false,
      allowVideo = false,
      allowDocuments = false,
      highlightProjectAutofillAttachment = false,
      hasPendingImage = false,
      imageUploadAcknowledged = false,
      onImageSelect,
      onImageClear,
      pendingImagePreview = null,
      pendingAttachmentMime = null,
      pendingAttachmentFileName = null,
      pendingAttachmentSizeBytes = null,
      lastAnalyzedDocumentationFileName = null,
      readyToExecute = false,
      onConfirmExecution,
      showDraftPublish = false,
      draftPublishing = false,
      onPublishDraft,
      onOpenHistory,
      historyCount
    },
    ref
  ) => {
    const fileInputRef = useRef<HTMLInputElement>(null)
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const valueRef = useRef(value)
    const onChangeRef = useRef(onChange)
    const fileInputId = useId()
    valueRef.current = value
    onChangeRef.current = onChange

    useImperativeHandle(
      ref,
      () => ({
        openAttachmentPicker: () => {
          if (!showImageUpload) return
          fileInputRef.current?.click()
        }
      }),
      [showImageUpload]
    )

    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (disabled || !isPrintableKey(e)) return
        if (isEditableFocused()) return

        e.preventDefault()
        textareaRef.current?.focus()
        onChangeRef.current(valueRef.current + e.key)
      }

      document.addEventListener('keydown', handleKeyDown, { capture: true })
      return () => document.removeEventListener('keydown', handleKeyDown, { capture: true })
    }, [disabled])

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file && onImageSelect) {
        onImageSelect(file)
      }
      e.target.value = ''
    }

    const canSend = value.trim() || hasPendingImage

    const handleSendButtonClick = () => {
      onSend()
    }

    const showPdfAttachmentPreview =
      pendingAttachmentMime === 'application/pdf' ||
      (pendingImagePreview?.startsWith('data:application/pdf') ?? false)
    const showPendingAttachment = hasPendingImage && !imageUploadAcknowledged
    const treatAsVideo =
      Boolean(pendingAttachmentMime?.startsWith('video/')) ||
      Boolean(pendingImagePreview?.startsWith('data:video/'))
    const treatAsSpreadsheet =
      pendingAttachmentMime === 'text/csv' ||
      pendingAttachmentMime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    const emphasizeAttachment =
      Boolean(allowDocuments && highlightProjectAutofillAttachment && !hasPendingImage)
    const showHistory =
      typeof onOpenHistory === 'function' &&
      typeof historyCount === 'number' &&
      historyCount > 0
    const historyLabel = historyCount && historyCount > 99 ? '99+' : String(historyCount ?? '')
    const fileAccept = allowVideo
      ? 'image/jpeg,image/png,image/webp,video/mp4,video/webm'
      : allowDocuments
        ? PROJECT_AUTOFILL_DOCUMENT_ACCEPT
        : 'image/jpeg,image/png,image/webp'
    const uploadLabelClass = [styles.imageUploadBtn, emphasizeAttachment ? styles.imageUploadBtnEmphasis : '']
      .filter(Boolean)
      .join(' ')

    const pendingDocCompare = useMemo(() => {
      const pending = pendingAttachmentFileName?.trim()
      const last = lastAnalyzedDocumentationFileName?.trim()
      if (!pending || !last) return null
      return normalizeFileNameForCompare(pending) === normalizeFileNameForCompare(last)
        ? ('same' as const)
        : ('different' as const)
    }, [pendingAttachmentFileName, lastAnalyzedDocumentationFileName])

    const pendingSizeLabel =
      typeof pendingAttachmentSizeBytes === 'number' && pendingAttachmentSizeBytes > 0
        ? formatCompactFileSize(pendingAttachmentSizeBytes)
        : ''

    return (
      <div className={styles.inputWrapper}>
        <div className={styles.inputContainer}>
          {showImageUpload && (
            <>
              <input
                ref={fileInputRef}
                id={fileInputId}
                type="file"
                accept={fileAccept}
                className={styles.fileInputHidden}
                onChange={handleImageChange}
                disabled={disabled}
                aria-hidden
                tabIndex={-1}
              />
              {showPendingAttachment ? (
                <div className={styles.pendingAttachmentRow}>
                  <div className={styles.pendingThumbWrap}>
                    <div
                      className={styles.pendingImagePreview}
                      aria-label={
                        showPdfAttachmentPreview
                          ? 'PDF listo para enviar'
                          : treatAsSpreadsheet
                            ? 'Archivo listo para enviar'
                            : treatAsVideo
                              ? 'Video listo para enviar'
                              : 'Archivo listo para enviar'
                      }
                    >
                      {treatAsVideo && pendingImagePreview ? (
                        <video src={pendingImagePreview} controls />
                      ) : showPdfAttachmentPreview || treatAsSpreadsheet ? (
                        <div className={styles.pendingPdfIcon}>
                          <FileText size={20} strokeWidth={1.85} aria-hidden />
                        </div>
                      ) : pendingImagePreview ? (
                        <img src={pendingImagePreview} alt="Vista previa" />
                      ) : (
                        <div className={styles.pendingPdfIcon}>
                          <FileText size={20} strokeWidth={1.85} aria-hidden />
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className={styles.clearImageBtn}
                      onClick={onImageClear}
                      aria-label="Quitar archivo"
                    >
                      <X size={12} />
                    </button>
                  </div>
                  <div className={styles.pendingAttachmentMeta}>
                    {pendingAttachmentFileName ? (
                      <span
                        className={styles.pendingFileName}
                        title={pendingAttachmentFileName}
                      >
                        {pendingAttachmentFileName}
                      </span>
                    ) : (
                      <span className={styles.pendingFileNameMuted}>Archivo listo para enviar</span>
                    )}
                    {pendingSizeLabel ? (
                      <span className={styles.pendingFileSize}>{pendingSizeLabel}</span>
                    ) : null}
                    {allowDocuments && pendingDocCompare === 'same' ? (
                      <span className={styles.pendingDocCompareSame}>
                        Mismo nombre que la última documentación analizada en esta charla
                      </span>
                    ) : null}
                    {allowDocuments && pendingDocCompare === 'different' ? (
                      <span className={styles.pendingDocCompareDiff}>
                        Distinto del último archivo analizado en esta charla
                      </span>
                    ) : null}
                  </div>
                </div>
              ) : !hasPendingImage ? (
                <label
                  htmlFor={fileInputId}
                  className={uploadLabelClass}
                  title={allowVideo ? 'Agregar imagen o video' : allowDocuments ? 'Agregar archivo' : 'Agregar imagen'}
                  aria-label={allowVideo ? 'Agregar imagen o video' : allowDocuments ? 'Agregar archivo' : 'Agregar imagen'}
                >
                  <ImagePlus size={20} strokeWidth={1.8} />
                </label>
              ) : null}
            </>
          )}
          <textarea
            ref={textareaRef}
            className={styles.input}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyPress}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
          />
          {showHistory && (
            <button
              type="button"
              className={styles.historyButton}
              onClick={onOpenHistory}
              aria-label="Ver conversaciones anteriores"
              title="Historial de conversaciones"
            >
              <MessageSquare size={18} />
              <span className={styles.historyBadge} aria-hidden="true">
                {historyLabel}
              </span>
            </button>
          )}
          {showDraftPublish && onPublishDraft ? (
            <button
              type="button"
              className={draftPublishStyles.draftPublishButton}
              onClick={onPublishDraft}
              disabled={disabled || draftPublishing}
              aria-label="Publicar obra"
            >
              {draftPublishing ? (
                <span>Publicando...</span>
              ) : (
                <>
                  <Rocket size={18} strokeWidth={1.8} aria-hidden />
                  <span>Publicar obra</span>
                </>
              )}
            </button>
          ) : null}
          {readyToExecute && onConfirmExecution ? (
            <button
              className={styles.confirmButton}
              onClick={onConfirmExecution}
              disabled={disabled}
              type="button"
              aria-label="Confirmar creación"
            >
              <Check size={18} />
              <span>Confirmar</span>
            </button>
          ) : (
            <button
              className={styles.sendButton}
              onClick={handleSendButtonClick}
              disabled={disabled || !canSend}
              type="button"
              aria-label="Enviar mensaje"
            >
              <SendHorizonal size={18} />
            </button>
          )}
        </div>
        {emphasizeAttachment ? (
          <p className={styles.attachmentHint} role="status">
            Adjuntá un archivo (PDF, imagen, CSV o Excel) para crear el borrador de obra.
          </p>
        ) : null}
      </div>
    )
  }
)

ConversationInput.displayName = 'ConversationInput'

export default ConversationInput
